import { extname, isAbsolute, relative, resolve } from "path";

const STATIC_IMMUTABLE_EXTENSIONS = new Set([
  ".avif",
  ".br",
  ".css",
  ".csv",
  ".eot",
  ".gif",
  ".gz",
  ".ico",
  ".jpeg",
  ".jpg",
  ".js",
  ".json",
  ".m4a",
  ".map",
  ".mjs",
  ".mov",
  ".mp3",
  ".mp4",
  ".ogg",
  ".pdf",
  ".png",
  ".svg",
  ".tsv",
  ".ttf",
  ".txt",
  ".wasm",
  ".wav",
  ".webm",
  ".webmanifest",
  ".webp",
  ".woff",
  ".woff2",
  ".xml",
  ".zip",
]);

const STATIC_DOCUMENT_EXTENSIONS = new Set([".htm", ".html"]);
const STATIC_NO_FALLBACK_EXTENSIONS = new Set([
  ...STATIC_DOCUMENT_EXTENSIONS,
  ...STATIC_IMMUTABLE_EXTENSIONS,
]);

export type StaticFileResolution =
  | { kind: "file"; filePath: string; cacheControl: string }
  | { kind: "forbidden" }
  | { kind: "not_found" };

export function isStaticAssetRoute(pathname: string): boolean {
  const normalizedPath = pathname || "/";
  return normalizedPath === "/assets" || normalizedPath.startsWith("/assets/");
}

export function isStaticReservedRuntimeRoute(pathname: string): boolean {
  const normalized = pathname || "/";
  return (
    /^\/(api|platform|internal|ws)(\/|$)/.test(normalized) ||
    normalized === "/_gencow/analytics.js" ||
    normalized === "/_gencow/analytics/event"
  );
}

function getLeafExtension(pathname: string): string {
  const leafName = (pathname || "/").split("/").filter(Boolean).at(-1) ?? "";
  return extname(leafName).toLowerCase();
}

export function hasKnownStaticFileExtension(pathname: string): boolean {
  const extension = getLeafExtension(pathname);
  return extension !== "" && STATIC_NO_FALLBACK_EXTENSIONS.has(extension);
}

export function shouldUseStaticIndexFallbackForMissingPath(pathname: string): boolean {
  if (isStaticReservedRuntimeRoute(pathname)) return false;
  if (isStaticAssetRoute(pathname)) return false;
  return !hasKnownStaticFileExtension(pathname);
}

export function isResolvedPathInsideDirectory(rootDir: string, resolvedPath: string): boolean {
  const relativePath = relative(resolve(rootDir), resolvedPath);
  return (
    relativePath === "" || (!!relativePath && !relativePath.startsWith("..") && !isAbsolute(relativePath))
  );
}

export function getStaticFileCacheControl(filePath: string): string {
  const extension = extname(filePath).toLowerCase();
  if (STATIC_DOCUMENT_EXTENSIONS.has(extension)) return "no-cache";
  if (STATIC_IMMUTABLE_EXTENSIONS.has(extension)) return "public, max-age=31536000, immutable";
  return "no-cache";
}

function toStaticRequestPath(pathname: string): string {
  if (!pathname || pathname === "/") return "index.html";
  return pathname.replace(/^\/+/, "");
}

async function fileExists(
  path: string,
  exists: (path: string) => boolean | Promise<boolean>,
): Promise<boolean> {
  return Boolean(await exists(path));
}

export async function resolveStaticFileRequest(params: {
  staticDir: string;
  pathname: string;
  exists: (path: string) => boolean | Promise<boolean>;
}): Promise<StaticFileResolution> {
  const staticRoot = resolve(params.staticDir);
  const requestedPath = toStaticRequestPath(params.pathname);
  const exactPath = resolve(staticRoot, requestedPath);

  if (!isResolvedPathInsideDirectory(staticRoot, exactPath)) {
    return { kind: "forbidden" };
  }
  if (isStaticReservedRuntimeRoute(params.pathname)) {
    return { kind: "not_found" };
  }

  if (await fileExists(exactPath, params.exists)) {
    return { kind: "file", filePath: exactPath, cacheControl: getStaticFileCacheControl(exactPath) };
  }

  const canUseDocumentCandidates =
    !isStaticAssetRoute(params.pathname) && !hasKnownStaticFileExtension(params.pathname);
  if (canUseDocumentCandidates) {
    const htmlPath = resolve(`${exactPath}.html`);
    if (isResolvedPathInsideDirectory(staticRoot, htmlPath) && (await fileExists(htmlPath, params.exists))) {
      return { kind: "file", filePath: htmlPath, cacheControl: getStaticFileCacheControl(htmlPath) };
    }
  }

  if (canUseDocumentCandidates) {
    const directoryIndexPath = resolve(exactPath, "index.html");
    if (
      isResolvedPathInsideDirectory(staticRoot, directoryIndexPath) &&
      (await fileExists(directoryIndexPath, params.exists))
    ) {
      return {
        kind: "file",
        filePath: directoryIndexPath,
        cacheControl: getStaticFileCacheControl(directoryIndexPath),
      };
    }
  }

  if (!shouldUseStaticIndexFallbackForMissingPath(params.pathname)) {
    return { kind: "not_found" };
  }

  const spaIndexPath = resolve(staticRoot, "index.html");
  if (await fileExists(spaIndexPath, params.exists)) {
    return { kind: "file", filePath: spaIndexPath, cacheControl: getStaticFileCacheControl(spaIndexPath) };
  }

  return { kind: "not_found" };
}
